import { Canvas, currentAccountIdAtom } from "@zapier/canvas-state";
import { atom } from "jotai";
import { atomFamily, atomWithRefresh, loadable } from "jotai/utils";

export const CANVAS_LIST_LIMIT = 10;

export const pageIndexAtom = atom(0);
pageIndexAtom.debugLabel = "pageIndexAtom";

export const visitedPagesAtom = atom<number[]>([]);
visitedPagesAtom.debugLabel = "visitedPagesAtom";

export type CanvasListMeta = {
  count: number;
  limit: number;
  offset: number;
};

// Create a family of atoms, one per page, for caching
export const canvasesByPageAtomFamily_SUSPENDS = atomFamily(
  (pageIndex: number) => {
    const baseAtom = atomWithRefresh(async () => {
      const res = await fetch(
        `/api/canvas/v1/canvases/?offset=${pageIndex * CANVAS_LIST_LIMIT}&limit=${CANVAS_LIST_LIMIT}`,
      );
      const json = await res.json();
      return json as { results: Canvas[]; meta: CanvasListMeta };
    });

    baseAtom.debugLabel = `canvasesByPageAtomFamily_SUSPENDS(${pageIndex})`;
    return baseAtom;
  },
);

// Create a derived atom that uses the current page index
export const canvasesByPageResponseAsyncAtom = atom((get) => {
  const pageIndex = get(pageIndexAtom);
  return get(canvasesByPageAtomFamily_SUSPENDS(pageIndex));
});
canvasesByPageResponseAsyncAtom.debugLabel = "canvasesByPageResponseAsyncAtom";

// Does NOT suspend due to loadable usage
export const canvasesByPageResponseLoadable = loadable(
  canvasesByPageResponseAsyncAtom,
);
canvasesByPageResponseLoadable.debugLabel = "canvasesByPageResponseLoadable";

const ownersByPageAtom_SUSPENDS = atom(async (get) => {
  const currentAccountId = get(currentAccountIdAtom);
  if (!currentAccountId) {
    return [];
  }
  const { results } = await get(canvasesByPageResponseAsyncAtom);
  const uniqueOwnerIds: number[] = Array.from(
    new Set(results.map((canvas) => canvas.customuser_id!)),
  );
  return await fetchMembers({
    accountId: currentAccountId,
    ids: uniqueOwnerIds,
  });
});
ownersByPageAtom_SUSPENDS.debugLabel = "ownersByPageAtomFamily";

// Does NOT suspend due to loadable usage
export const ownersByPageAtom = loadable(ownersByPageAtom_SUSPENDS);
ownersByPageAtom.debugLabel = "ownersByPageAtom";

async function fetchMembers({
  accountId,
  ids = [],
}: {
  accountId: number;
  ids: number[];
}) {
  const url = `/api/v4/accounts/${accountId}/members/`;

  const params = {
    ids: ids.join(","),
    limit: String(ids.length),
  };

  // Construct query string from params
  const queryString = new URLSearchParams(params).toString();
  const fullUrl = `${url}?${queryString}`;

  const res = await fetch(fullUrl);
  const json = await res.json();
  return json.results;
}

// Atom to mark higher visited pages for refresh.
// The actual refresh/fetch will occur when those pages are next accessed.
export const refreshHigherVisitedPagesSetter = atom(null, (get, set) => {
  const currentPageIndex = get(pageIndexAtom);
  const visitedPages = get(visitedPagesAtom);

  visitedPages.forEach((visitedPageIndex) => {
    if (visitedPageIndex > currentPageIndex) {
      set(canvasesByPageAtomFamily_SUSPENDS(visitedPageIndex));
    }
  });
});
